import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  accountQuery,
  buildCasinoGameFilters,
  casinoGamesQuery,
  launchCasinoGame,
  useAuthSession,
} from '@/api';
import { casinoNavSlug, type CasinoGame } from '@/domain';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { GameDetailsPanel } from './GameDetailsPanel';

export interface CasinoPlayPageProps {
  category: string;
  provider: string;
  vendor: string;
  gameTag: string;
  gameName?: string;
}

export function CasinoPlayPage({
  category,
  provider,
  vendor,
  gameTag,
  gameName,
}: CasinoPlayPageProps) {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { loggedIn, user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());

  const decodedTag = decodeURIComponent(gameTag);
  const decodedName = gameName ? decodeURIComponent(gameName) : decodedTag;
  const isLive = casinoNavSlug(category) === 'livecasino';
  const vendorSlug = casinoNavSlug(vendor);
  const providerSlug = casinoNavSlug(provider);

  const [gameUrl, setGameUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);

  const catalogueFilters = useMemo(
    () =>
      buildCasinoGameFilters({
        account,
        user,
        category,
        vendorOrFlag: vendor,
        liveMode: isLive,
      }),
    [account, user, category, vendor, isLive],
  );

  const catalogueQuery = useQuery({
    ...casinoGamesQuery(catalogueFilters, 48),
    enabled: Boolean(catalogueFilters.AccountGuid),
  });

  const resolvedGame = useMemo((): CasinoGame | null => {
    const rows = catalogueQuery.data?.Data ?? [];
    const tagKey = casinoNavSlug(decodedTag);
    const byTag = rows.find((row) => casinoNavSlug(row.GameTag) === tagKey);
    if (byTag) return byTag;
    const nameKey = decodedName.trim().toLowerCase();
    return rows.find((row) => row.Name.trim().toLowerCase() === nameKey) ?? null;
  }, [catalogueQuery.data, decodedTag, decodedName]);

  const launchMutation = useMutation({
    mutationFn: () =>
      launchCasinoGame({
        provider: providerSlug || provider,
        // Legacy MenuItem — URL vendor slug (e.g. `pragmatic`), not display name.
        vendor: vendorSlug || vendor,
        gameTag: decodedTag,
        category,
        isMobile: !isDesktop,
        account,
        user,
        language: user?.Language ?? account?.Language?.Short,
      }),
    onSuccess: (url) => {
      if (!isDesktop) {
        window.location.href = url;
        return;
      }
      setGameUrl(url);
      setPlaying(true);
    },
  });

  useEffect(() => {
    setGameUrl(null);
    setPlaying(false);
    launchMutation.reset();
    // Reset only when the route target game changes — not when mutation identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional route-key deps
  }, [category, provider, vendor, gameTag, gameName]);

  const playError =
    launchMutation.error instanceof Error
      ? launchMutation.error.message
      : launchMutation.error
        ? 'Unable to start game'
        : null;

  const onPlay = () => {
    if (!loggedIn) {
      const returnTo = window.location.pathname + window.location.search;
      void navigate({
        to: '/login',
        search: { redirect: returnTo },
      });
      return;
    }
    if (isDesktop) setPlaying(true);
    launchMutation.mutate();
  };

  const onStopPlay = () => {
    setGameUrl(null);
    setPlaying(false);
    launchMutation.reset();
  };

  const onBackFromDetails = () => {
    const cat = casinoNavSlug(category) || 'slots';
    const vendorSegment = casinoNavSlug(resolvedGame?.VendorName ?? vendor);
    void navigate({
      to: vendorSegment ? `/casino/${cat}/${vendorSegment}` : `/casino/${cat}`,
    });
  };

  return (
    <GameDetailsPanel
      title={resolvedGame?.Name ?? decodedName}
      provider={resolvedGame?.ProviderName ?? provider}
      vendor={resolvedGame?.VendorName ?? vendor}
      gameTag={decodedTag}
      category={category}
      game={resolvedGame}
      playLoading={launchMutation.isPending}
      playError={playError}
      gameUrl={isDesktop ? gameUrl : null}
      playing={isDesktop && playing}
      tallPlayer={isLive}
      onPlay={onPlay}
      onBack={onBackFromDetails}
      onStopPlay={onStopPlay}
      onRetryPlay={() => launchMutation.mutate()}
      onOpenWindow={
        gameUrl
          ? () => {
              window.open(gameUrl, '_blank', 'noopener,noreferrer');
            }
          : undefined
      }
    />
  );
}
